import { createContext } from 'react'

/**
 * Toast 表示用の React Context。
 *
 * Provider は `./ToastProvider`、hook は `./useToast` に分離。
 * Component 以外を export するファイルを Provider から切り出すことで
 * `react-refresh/only-export-components` (Vite Fast Refresh) を満たす。
 */

export type ToastTone = 'info' | 'success' | 'warn' | 'danger'

export interface ToastShowOptions {
  /** 既定 'info'。 */
  tone?: ToastTone

  /** タイトル文字列の i18n key (必須)。例: `'common.toast.copy_success'` */
  titleKey: string

  /**
   * `titleKey` の interpolation 用変数 (T-D-Vault-A 追加)。
   * 例: `titleKey: 'vault.toast.copied_password'` + `titleVars: { ttl: 90 }`
   *     → 「パスワードをコピーしました (90 秒後に自動消去)」のように変数展開する。
   */
  titleVars?: Record<string, string | number>

  /** 説明文 (任意) の i18n key。 */
  descKey?: string

  /** `descKey` の interpolation 用変数 (T-D-Vault-A 追加、titleVars と同じ用途)。 */
  descVars?: Record<string, string | number>

  /** 自動消去までの ms。既定 5000。`0` で永続表示。 */
  ttlMs?: number

  /**
   * `true` のとき、同じ `titleKey` を持つ toast が既に stack 内に存在すれば
   * 新規追加せずに既存 toast の id を返す。連続 401 等で同一エラーメッセージが
   * 何度も積まれる UX 問題 (旧 L-19 / T-C1 R-5) の対策。
   * 既定は `false` (常に新規追加)。
   */
  dedupe?: boolean
}

export interface Toast {
  readonly id: string
  readonly tone: ToastTone
  readonly titleKey: string
  readonly titleVars?: Readonly<Record<string, string | number>>
  readonly descKey?: string
  readonly descVars?: Readonly<Record<string, string | number>>
}

export interface ToastContextValue {
  /** Toast を追加して id を返す。 */
  show: (opts: ToastShowOptions) => string

  /** 指定 id の toast を即時消去 (タイマも clear)。 */
  dismiss: (id: string) => void
}

export const ToastContext = createContext<ToastContextValue | null>(null)
